import React, { useEffect, useMemo, useState } from 'react';
import api from '../api/kiteApi';
import {
  allocateTargetQuantities,
  DEFAULT_TRADE_CONFIG,
  normalizeTradeConfig,
  STOPLOSS_MOVEMENT_ACTIONS,
} from '../utils/tradeConfig';

function formatNumber(value) {
  return new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 }).format(Number(value) || 0);
}

function formatDateTime(value) {
  if (!value) return 'Never';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? 'Never' : parsed.toLocaleString();
}

function notificationStatusLabel(position) {
  if (position.notification_error) return 'Notification review';
  if (!position.notifications_enabled) return 'Notifications off';
  if (position.stoploss_notification_sent_at) return 'SL alert sent';
  const hasTargetAlert = (position.target_prices || []).some((target) => target.notification_sent_at);
  if (hasTargetAlert) return 'Target alert sent';
  if (position.notification_last_result === 'SENDING') return 'Sending notification';
  if (position.notification_last_result === 'FAILED') return 'Notification failed';
  if (position.notification_last_result === 'SKIPPED') return 'Notification skipped';
  return 'Notifications armed';
}

function notificationStatusClass(position) {
  const label = notificationStatusLabel(position);
  if (label === 'Notification review' || label === 'Notification failed' || label === 'Notification skipped') return 'plan-status-review';
  if (label === 'Notifications armed' || label === 'Sending notification') return 'plan-status-live';
  if (label === 'SL alert sent' || label === 'Target alert sent') return 'plan-status-review';
  return 'plan-status-idle';
}

function liveExecutionStatusLabel(position) {
  if (position.live_execution_error) return 'Execution review';
  if (!position.live_execution_enabled) return 'Live execution off';
  if (position.live_execution_pending_order) return 'Order pending';
  if (position.live_execution_order_blocked) return 'Execution paused';
  if (position.live_execution_plan_closed || String(position.status || '').toUpperCase() === 'CLOSED') return 'Execution closed';
  if (position.live_execution_last_result === 'DELIVERED') return 'Execution active';
  if (position.live_execution_last_result === 'FAILED') return 'Execution failed';
  if (position.live_execution_last_action === 'TARGET_EXIT') return 'Target executed';
  if (position.live_execution_last_action === 'STOPLOSS_EXIT') return 'SL executed';
  if (position.live_execution_last_result === 'SKIPPED') return 'Execution skipped';
  return 'Execution armed';
}

function liveExecutionStatusClass(position) {
  const label = liveExecutionStatusLabel(position);
  if (label === 'Execution review' || label === 'Execution failed' || label === 'Execution skipped' || label === 'GTT review') return 'plan-status-review';
  if (label === 'Execution armed' || label === 'Execution active' || label === 'Order pending' || label === 'GTT armed' || label === 'GTT waiting') return 'plan-status-live';
  if (label === 'Target executed' || label === 'SL executed' || label === 'Execution closed') return 'plan-status-idle';
  if (label.startsWith('GTT')) return 'plan-status-idle';
  return 'plan-status-idle';
}

function getLiveExecutionFlow(position, draft = null) {
  const quantity = getPlanQuantity(position);
  const targets = Array.isArray(position.target_prices) ? position.target_prices : [];
  const stoplossMovementPlan = Array.isArray(draft?.stoploss_movement_plan) && draft.stoploss_movement_plan.length > 0
    ? draft.stoploss_movement_plan
    : [];
  const initialStoploss = Number(position.initial_stoploss_price ?? position.saved_stoploss);
  const projectedExitQuantity = (target) => {
    const executedQty = Number(target.live_execution_executed_quantity);
    if (Number.isFinite(executedQty) && executedQty > 0) return executedQty;
    const configuredQty = Number(target.quantity);
    if (Number.isFinite(configuredQty) && configuredQty > 0) return configuredQty;
    return 0;
  };
  const targetEntries = targets.map((target, index) => {
    const ratio = Number(target.ratio) || index + 1;
    const configuredQty = Number(target.quantity);
    const exitedQty = Number(target.live_execution_executed_quantity) || 0;
    const isExecuted = Boolean(target.live_execution_executed_at);
    const price = Number(target.price);
    const remainingAfterTarget = Math.max(0, quantity - targets
      .slice(0, index + 1)
      .reduce((sum, current) => sum + projectedExitQuantity(current), 0));
    let status = 'Yet to hit';
    if (isExecuted) status = exitedQty > 0 ? `T${ratio} hit, qty exited ${exitedQty}` : `T${ratio} hit`;
    else if (exitedQty > 0) status = `T${ratio} partial, qty exited ${exitedQty}`;
    else if (position.live_execution_last_check_price != null && Number.isFinite(price) && quantity > 0) {
      const isLong = getQuantity(position) > 0;
      const lastPrice = Number(position.live_execution_last_check_price);
      const targetTouched = isLong ? lastPrice >= price : lastPrice <= price;
      if (targetTouched) status = `T${ratio} pending execution`;
    }
    const movementAction = String(stoplossMovementPlan[index] || '').toUpperCase();
    let slMoveLabel = 'SL unchanged';
    if (movementAction === 'ENTRY_PRICE') slMoveLabel = 'SL moves to Entry';
    else if (movementAction === 'PREVIOUS_TARGET') slMoveLabel = index > 0 ? `SL moves to T${ratio - 1}` : 'SL moves to initial SL';
    else if (movementAction === 'HIT_TARGET') slMoveLabel = `SL moves to T${ratio}`;
    else if (movementAction === 'KEEP') slMoveLabel = 'SL stays unchanged';
    return {
      key: `target-${ratio}-${index}`,
      title: `T${ratio}`,
      price,
      quantity: Number.isFinite(configuredQty) && configuredQty > 0 ? configuredQty : null,
      status,
      remainingAfterTarget,
      done: isExecuted,
      executedAt: target.live_execution_executed_at || null,
      executedQuantity: Number.isFinite(exitedQty) && exitedQty > 0 ? exitedQty : null,
      orderId: target.live_execution_order_id || null,
      executionError: target.live_execution_error || null,
      slMoveLabel,
    };
  });

  const stoplossTriggered = Boolean(position.live_execution_stoploss_executed_at);
  const stoplossPrice = Number(position.trailing_stoploss_price ?? position.saved_stoploss);
  const remainingQuantity = Number(position.live_execution_remaining_quantity);
  const openRemaining = Number.isFinite(remainingQuantity) ? remainingQuantity : Math.max(0, quantity - targetEntries.reduce((sum, item) => sum + (item.done ? (item.quantity || 0) : 0), 0));
  const executedTargetCount = targetEntries.filter((item) => item.done).length;
  const currentStepIndex = stoplossTriggered
    ? 0
    : (position.live_execution_last_action === 'TARGET_EXIT'
      ? Math.min(executedTargetCount + 1, targetEntries.length)
      : 0);
  let currentSlLabel = 'Current SL unchanged';
  if (stoplossTriggered) currentSlLabel = 'SL hit and all remaining qty exited';
  else if (executedTargetCount === 0) currentSlLabel = Number.isFinite(initialStoploss) && initialStoploss > 0 ? `Current SL at Rs${formatNumber(initialStoploss)}` : 'Current SL not set';
  else {
    const lastExecutedIndex = Math.max(0, executedTargetCount - 1);
    currentSlLabel = targetEntries[lastExecutedIndex]?.slMoveLabel || currentSlLabel;
  }
  const stoplossStatus = stoplossTriggered
    ? `SL hit, qty exited ${quantity > 0 ? Math.max(0, quantity - openRemaining) || quantity : quantity}`
    : 'SL not yet triggered';

  return {
    targetEntries,
    stoploss: {
      title: 'SL',
      price: stoplossPrice,
      status: stoplossStatus,
      done: stoplossTriggered,
      remaining: openRemaining,
      moveLabel: currentSlLabel,
    },
    currentStepIndex,
    summary: position.live_execution_error
      ? 'API error'
      : (position.live_execution_plan_closed || String(position.status || '').toUpperCase() === 'CLOSED'
        ? 'Complete - all remaining qty exited'
        : (position.live_execution_last_action === 'TARGET_EXIT' && openRemaining > 0
          ? 'Partially target exited'
          : 'Monitoring active')),
  };
}

function getLiveExecutionDiagnostic(position, flow) {
  if (!position.live_execution_enabled) return { state: 'OFF', reason: 'Live execution is disabled for this plan.' };
  if (position.live_execution_pending_order) return { state: 'WAITING', reason: `Waiting for Kite order ${position.live_execution_pending_order.order_id || position.live_execution_pending_order.tag || ''} to finish (${position.live_execution_pending_order.status || 'SUBMITTING'}).` };
  if (position.live_execution_order_blocked) return { state: 'ERROR', reason: position.live_execution_error || 'Execution paused after a rejected or cancelled Kite order. Review the order, then save live execution settings to resume.' };
  if (position.live_execution_error) return { state: 'ERROR', reason: position.live_execution_error };
  if (position.live_execution_plan_closed || String(position.status || '').toUpperCase() === 'CLOSED') return { state: 'CLOSED', reason: 'Plan is closed; reset the plan before monitoring again.' };
  const ltp = Number(position.live_execution_last_check_price);
  if (!Number.isFinite(ltp) || ltp <= 0) return { state: 'WAITING', reason: 'Waiting for the first valid Kite LTP.' };
  const sl = Number(flow?.stoploss?.price);
  const isLong = getQuantity(position) > 0;
  const activeSlText = Number.isFinite(sl) && sl > 0 ? `Active SL ₹${formatNumber(sl)}` : 'Active SL not set';
  const pendingTargets = (flow?.targetEntries || []).filter((target) => !target.done && Number.isFinite(Number(target.price)))
    .map((target) => `T${target.title.replace('T', '')} ₹${formatNumber(target.price)}${target.quantity ? ` × ${target.quantity}` : ''}`)
    .join(' · ');
  const targetText = pendingTargets || 'No pending targets';
  const slHit = Number.isFinite(sl) && (isLong ? ltp <= sl : ltp >= sl);
  if (slHit) return { state: 'READY', reason: `Stop-loss condition met: LTP ₹${formatNumber(ltp)} vs SL ₹${formatNumber(sl)}. ${activeSlText} · ${targetText}` };
  const pendingTarget = (flow?.targetEntries || []).find((target) => !target.done && Number.isFinite(Number(target.price)) && (isLong ? ltp >= Number(target.price) : ltp <= Number(target.price)));
  if (pendingTarget) return { state: 'READY', reason: `T${pendingTarget.title.replace('T', '')} condition met: LTP ₹${formatNumber(ltp)} vs target ₹${formatNumber(pendingTarget.price)}. ${activeSlText} · ${targetText}` };
  return { state: 'CHECKED', reason: `No trigger: LTP ₹${formatNumber(ltp)} has not reached the active SL or any pending target. ${activeSlText} · ${targetText}` };
}

function formatTimestamp(value) {
  if (!value) return 'Never';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? 'Never' : parsed.toLocaleString();
}

function isLiveExecutionCheckStale(position) {
  if (!position.live_execution_enabled || position.live_execution_plan_closed || !position.live_execution_last_check_at) return false;
  const checkedAt = new Date(position.live_execution_last_check_at).getTime();
  return Number.isFinite(checkedAt) && Date.now() - checkedAt > 30000;
}

function stoplossMovementActionLabel(action) {
  const normalized = String(action || '').toUpperCase();
  if (normalized === 'KEEP') return 'Keep current SL';
  if (normalized === 'ENTRY_PRICE') return 'Move to entry';
  if (normalized === 'HIT_TARGET') return 'Move to hit target';
  return 'Move to previous target';
}

function targetMovementLabel(target, index) {
  const ratio = Number(target?.ratio);
  if (Number.isFinite(ratio) && ratio > 0) return `After 1:${ratio}`;
  return `After target ${index + 1}`;
}

function normalizeStoplossPlan(plan, count) {
  return normalizeTradeConfig({
    target_count: Math.max(1, count || 1),
    stoploss_movement_plan_count: Math.max(1, count || 1),
    stoploss_movement_plan: plan,
  }).stoploss_movement_plan;
}

function positionKey(position) {
  return [position.exchange, position.tradingsymbol || position.trading_symbol, position.product]
    .filter(Boolean)
    .join(':');
}

function getQuantity(position) {
  if (String(position.product || position.product_type || '').toUpperCase() === 'CNC'
    && position.combined_quantity != null) {
    return Math.max(0, Number(position.combined_quantity) || 0);
  }
  const assetType = String(position.asset_type || '').toUpperCase();
  if (assetType === 'HOLDING') {
    const settledQuantity = Number(position.quantity) || 0;
    const t1Quantity = Number(position.t1_quantity) || 0;
    return settledQuantity + t1Quantity;
  }
  return Number(position.quantity ?? position.net_quantity) || 0;
}

function getPlanQuantity(position) {
  if (String(position.product || position.product_type || '').toUpperCase() === 'CNC'
    && position.combined_quantity != null) {
    return Math.max(0, Number(position.combined_quantity) || 0);
  }
  return Math.abs(getQuantity(position));
}

function buildQuoteKey(exchange, tradingsymbol) {
  return `${String(exchange || 'NSE').toUpperCase()}:${String(tradingsymbol || '').toUpperCase()}`;
}

function getCurrentRiskAmount(position, draft = null) {
  const quantity = getPlanQuantity(position);
  const entryPrice = Number(draft?.entry_price ?? position.entry_price ?? position.avg_price ?? position.average_price);
  const stoplossPrice = Number(
    draft?.trailing_stoploss_price
    ?? position.trailing_stoploss_price
    ?? draft?.initial_stoploss_price
    ?? position.initial_stoploss_price
    ?? position.saved_stoploss,
  );
  if (!Number.isFinite(quantity) || quantity <= 0) return null;
  if (!Number.isFinite(entryPrice) || !Number.isFinite(stoplossPrice) || entryPrice <= 0 || stoplossPrice <= 0) return null;
  return Math.abs(entryPrice - stoplossPrice) * quantity;
}

function getLiveRiskAmount(position, draft = null) {
  const quantity = getPlanQuantity(position);
  const lastPrice = Number(position.last_price);
  const stoplossPrice = Number(
    draft?.trailing_stoploss_price
    ?? position.trailing_stoploss_price
    ?? draft?.initial_stoploss_price
    ?? position.initial_stoploss_price
    ?? position.saved_stoploss,
  );
  if (!Number.isFinite(quantity) || quantity <= 0) return null;
  if (!Number.isFinite(lastPrice) || !Number.isFinite(stoplossPrice) || lastPrice <= 0 || stoplossPrice <= 0) return null;
  return Math.abs(lastPrice - stoplossPrice) * quantity;
}

function buildConfiguredTargets(position, tradeConfig, initialStoploss, existingTargets = [], configuredEntryPrice = null) {
  const config = normalizeTradeConfig(tradeConfig || DEFAULT_TRADE_CONFIG);
  const totalQuantity = getPlanQuantity(position);
  const quantities = allocateTargetQuantities(totalQuantity, config.target_percentages);
  const entryPrice = Number(configuredEntryPrice ?? position.entry_price ?? position.avg_price ?? position.average_price);
  const stoplossPrice = Number(initialStoploss);
  const risk = Math.abs(entryPrice - stoplossPrice);
  const direction = getQuantity(position) >= 0 ? 1 : -1;

  return Array.from({ length: config.target_count }, (_, index) => {
    const existing = existingTargets[index] || {};
    const calculatedPrice = Number.isFinite(entryPrice) && Number.isFinite(stoplossPrice) && risk > 0
      ? Number((entryPrice + (direction * risk * (index + 1))).toFixed(2))
      : '';
    return {
      ratio: Number(existing.ratio) || index + 1,
      price: existing.price ?? calculatedPrice,
      quantity: existing.quantity ?? (quantities[index] || ''),
      error: existing.error || null,
      notification_sent_at: existing.notification_sent_at || null,
      notification_sent_price: existing.notification_sent_price ?? null,
      suggested_percentage: config.target_percentages[index],
    };
  });
}

function getLatestExecutedTarget(position) {
  const targets = Array.isArray(position.target_prices) ? position.target_prices : [];
  return targets
    .filter((target) => target.live_execution_executed_at)
    .sort((left, right) => new Date(right.live_execution_executed_at).getTime() - new Date(left.live_execution_executed_at).getTime())[0] || null;
}

function getNextPendingTarget(position) {
  const targets = Array.isArray(position.target_prices) ? position.target_prices : [];
  return targets
    .filter((target) => !target.live_execution_executed_at && Number.isFinite(Number(target.price)) && Number(target.price) > 0)
    .sort((left, right) => Number(left.ratio || 0) - Number(right.ratio || 0))[0] || null;
}

function buildDraft(position, tradeConfig, marketSnapshot = null) {
  const suggestedDayLow = marketSnapshot && Number.isFinite(Number(marketSnapshot.day_low))
    ? Number(marketSnapshot.day_low)
    : '';
  const initialStoploss = position.initial_stoploss_price ?? position.saved_stoploss ?? suggestedDayLow ?? '';
  const existingTargets = Array.isArray(position.target_prices) ? position.target_prices : [];
  const shouldUseConfig = existingTargets.length === 0;
  return {
    entry_price: position.entry_price ?? position.avg_price ?? position.average_price ?? '',
    initial_stoploss_price: initialStoploss,
    trailing_stoploss_price: position.trailing_stoploss_price ?? initialStoploss,
    targets: shouldUseConfig
      ? buildConfiguredTargets(position, tradeConfig, initialStoploss, existingTargets, position.entry_price ?? position.avg_price ?? position.average_price)
      : existingTargets.map((target, index) => ({
        ratio: Number(target.ratio) || index + 1,
        price: target.price ?? '',
        quantity: target.quantity ?? '',
        error: target.error || null,
        notification_sent_at: target.notification_sent_at || null,
        notification_sent_price: target.notification_sent_price ?? null,
        live_execution_executed_at: target.live_execution_executed_at || null,
        live_execution_executed_price: target.live_execution_executed_price ?? null,
        live_execution_executed_quantity: target.live_execution_executed_quantity ?? null,
        live_execution_order_id: target.live_execution_order_id || null,
        live_execution_error: target.live_execution_error || null,
        suggested_percentage: null,
    })),
    notifications_enabled: Boolean(position.notifications_enabled),
    live_execution_enabled: Boolean(position.live_execution_enabled),
    live_execution_mode: 'MARKET',
    use_global_gtt_chase_config: position.use_global_gtt_chase_config !== false,
    gtt_sl_limit_buffer_percent: position.gtt_sl_limit_buffer_percent ?? '',
    gtt_order_chase_enabled: position.gtt_order_chase_enabled ?? true,
    gtt_order_chase_after_ms: position.gtt_order_chase_after_ms ?? 3000,
    gtt_order_chase_step_percent: position.gtt_order_chase_step_percent ?? 1,
    gtt_order_chase_max_attempts: position.gtt_order_chase_max_attempts ?? 2,
    gtt_order_chase_to_market: position.gtt_order_chase_to_market ?? false,
    gtt_target_order_chase_enabled: position.gtt_target_order_chase_enabled ?? false,
    move_stoploss_automatically: position.move_stoploss_automatically !== false,
    stoploss_movement_plan: normalizeStoplossPlan(
      Array.isArray(position.stoploss_movement_plan) && position.stoploss_movement_plan.length > 0
        ? position.stoploss_movement_plan
        : tradeConfig.stoploss_movement_plan,
      existingTargets.length || tradeConfig.target_count,
    ),
    exit_quantity: Math.abs(getQuantity(position)),
  };
}

export default function PositionsCard({
  positions = {},
  onSaved,
  loading = false,
  tradeConfig = DEFAULT_TRADE_CONFIG,
  title = 'Open positions',
  kicker = 'Synced from Kite',
  emptyTitle = 'No live positions',
  emptyText = 'Open Kite positions will sync into this Real Trades tab.',
  exitEndpoint = '/positions/exit',
  assetType = 'POSITION',
}) {
  const net = Array.isArray(positions) ? positions : (positions.net || []);
  const openPositions = useMemo(() => net.filter((position) => getPlanQuantity(position) > 0), [net]);
  const [marketSnapshots, setMarketSnapshots] = useState({});
  const [editing, setEditing] = useState({});
  const [saving, setSaving] = useState({});
  const [expanded, setExpanded] = useState({});
  const [flowExpanded, setFlowExpanded] = useState({});
  const [exiting, setExiting] = useState({});
  const [resetting, setResetting] = useState({});
  const [resettingAllPlans, setResettingAllPlans] = useState(false);
  const [cleaning, setCleaning] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    let active = true;

    if (openPositions.length === 0) {
      setMarketSnapshots({});
      return () => {
        active = false;
      };
    }

    const symbols = openPositions
      .map((position) => buildQuoteKey(position.exchange, position.tradingsymbol || position.trading_symbol))
      .filter(Boolean)
      .join(',');

    (async () => {
      try {
        const response = await api.get('/market/quote', {
          params: { symbols },
        });
        if (!active) return;
        const items = Array.isArray(response.data?.items) ? response.data.items : [];
        const nextSnapshots = items.reduce((acc, item) => {
          acc[buildQuoteKey(item.exchange, item.symbol)] = item;
          return acc;
        }, {});
        setMarketSnapshots(nextSnapshots);
      } catch (requestError) {
        if (active) setMarketSnapshots({});
      }
    })();

    return () => {
      active = false;
    };
  }, [openPositions]);

  const getDraft = (position) => {
    const key = positionKey(position);
    return editing[key] || buildDraft(
      position,
      tradeConfig,
      marketSnapshots[buildQuoteKey(position.exchange, position.tradingsymbol || position.trading_symbol)] || null,
    );
  };

  const getYahooCurrentPrice = (position) => {
    const snapshot = marketSnapshots[buildQuoteKey(position.exchange, position.tradingsymbol || position.trading_symbol)] || null;
    const currentPrice = Number(snapshot?.current_price);
    if (Number.isFinite(currentPrice) && currentPrice > 0) return currentPrice;
    return Number(position.last_price ?? position.close_price ?? 0);
  };

  const getYahooSnapshotPrice = (position) => {
    const snapshot = marketSnapshots[buildQuoteKey(position.exchange, position.tradingsymbol || position.trading_symbol)] || null;
    const currentPrice = Number(snapshot?.current_price);
    return Number.isFinite(currentPrice) && currentPrice > 0 ? currentPrice : null;
  };

  const updateDraft = (position, changes) => {
    const key = positionKey(position);
    setEditing((current) => ({
      ...current,
      [key]: { ...(current[key] || buildDraft(position, tradeConfig)), ...changes },
    }));
  };

  const isExpanded = (position, section) => {
    const stateKey = `${positionKey(position)}:${section}`;
    return expanded[stateKey] ?? section === 'notifications';
  };

  const toggleSection = (position, section) => {
    const stateKey = `${positionKey(position)}:${section}`;
    const nextExpanded = !(expanded[stateKey] ?? section === 'notifications');
    setExpanded((current) => ({ ...current, [stateKey]: nextExpanded }));
  };

  const toggleFlow = (position) => {
    const key = positionKey(position);
    setFlowExpanded((current) => ({ ...current, [key]: !(current[key] ?? false) }));
  };

  const saveNotifications = async (position) => {
    const key = positionKey(position);
    const draft = getDraft(position);
    setError(null);
    setMessage(null);
    setSaving((current) => ({ ...current, [`${key}:notifications`]: true }));
    try {
      await api.put(`/stoplosses/${encodeURIComponent(position.tradingsymbol)}/notifications`, {
        instrument_token: position.instrument_token,
        exchange: position.exchange,
        product_type: position.product,
        asset_type: position.plan_asset_type || position.asset_type || assetType,
        notifications_enabled: Boolean(draft.notifications_enabled),
        ...(draft.notifications_enabled ? { notification_plan_closed: false, notification_error: null } : {}),
      });
      setMessage(`${position.tradingsymbol} notification settings saved.`);
      if (typeof onSaved === 'function') await onSaved();
    } catch (requestError) {
      setError(requestError.response?.data?.error || requestError.message || 'Notification save failed');
    } finally {
      setSaving((current) => ({ ...current, [`${key}:notifications`]: false }));
    }
  };

  const saveLiveExecution = async (position) => {
    const key = positionKey(position);
    const draft = getDraft(position);
    const useGlobalGttChaseConfig = draft.use_global_gtt_chase_config !== false;
    const gttChaseNumbers = {
      gtt_sl_limit_buffer_percent: Number(draft.gtt_sl_limit_buffer_percent),
      gtt_order_chase_after_ms: Number(draft.gtt_order_chase_after_ms),
      gtt_order_chase_step_percent: Number(draft.gtt_order_chase_step_percent),
      gtt_order_chase_max_attempts: Number(draft.gtt_order_chase_max_attempts),
    };
    setError(null);
    setMessage(null);
    if (!useGlobalGttChaseConfig && Object.values(gttChaseNumbers).some((value) => !Number.isFinite(value) || value < 0)) {
      setError('GTT chase override values must be zero or positive numbers.');
      return;
    }
    setSaving((current) => ({ ...current, [`${key}:liveExecution`]: true }));
    try {
      await api.put(`/live-execution/plan/${encodeURIComponent(position.tradingsymbol)}`, {
        instrument_token: position.instrument_token,
        exchange: position.exchange,
        product_type: position.product,
        asset_type: position.plan_asset_type || position.asset_type || assetType,
        live_execution_enabled: Boolean(draft.live_execution_enabled),
        live_execution_mode: draft.live_execution_mode,
        use_global_gtt_chase_config: useGlobalGttChaseConfig,
        ...(!useGlobalGttChaseConfig ? {
          gtt_sl_limit_buffer_percent: gttChaseNumbers.gtt_sl_limit_buffer_percent,
          gtt_order_chase_enabled: Boolean(draft.gtt_order_chase_enabled),
          gtt_order_chase_after_ms: Math.round(gttChaseNumbers.gtt_order_chase_after_ms),
          gtt_order_chase_step_percent: gttChaseNumbers.gtt_order_chase_step_percent,
          gtt_order_chase_max_attempts: Math.round(gttChaseNumbers.gtt_order_chase_max_attempts),
          gtt_order_chase_to_market: Boolean(draft.gtt_order_chase_to_market),
          gtt_target_order_chase_enabled: Boolean(draft.gtt_target_order_chase_enabled),
        } : {}),
      });
      setMessage(`${position.tradingsymbol} live execution settings saved.`);
      if (typeof onSaved === 'function') await onSaved();
    } catch (requestError) {
      setError(requestError.response?.data?.error || requestError.message || 'Live execution save failed');
    } finally {
      setSaving((current) => ({ ...current, [`${key}:liveExecution`]: false }));
    }
  };

  const resetLiveExecution = async (position) => {
    const key = positionKey(position);
    if (!window.confirm(`Reset live execution state for ${position.tradingsymbol}?`)) return;
    setError(null);
    setMessage(null);
    setResetting((current) => ({ ...current, [`${key}:liveExecution`]: true }));
    try {
      await api.post(`/live-execution/plan/${encodeURIComponent(position.tradingsymbol)}/reset`, {
        instrument_token: position.instrument_token,
        exchange: position.exchange,
        product_type: position.product,
        asset_type: position.plan_asset_type || position.asset_type || assetType,
      });
      setMessage(`${position.tradingsymbol} live execution state reset.`);
      if (typeof onSaved === 'function') await onSaved();
    } catch (requestError) {
      setError(requestError.response?.data?.error || requestError.message || 'Live execution reset failed');
    } finally {
      setResetting((current) => ({ ...current, [`${key}:liveExecution`]: false }));
    }
  };

  const applyConfiguration = (position) => {
    const draft = getDraft(position);
    const completedTargets = draft.targets.filter((target) => target.notification_sent_at);
    if (completedTargets.length > 0) {
      setError('Configuration cannot replace targets after an alert plan has completed.');
      return;
    }
    updateDraft(position, {
      targets: buildConfiguredTargets(position, tradeConfig, draft.initial_stoploss_price, [], draft.entry_price),
    });
    setError(null);
    setMessage(`${position.tradingsymbol} targets populated from the global target plan.`);
  };

  const updateTarget = (position, targetIndex, field, value) => {
    const draft = getDraft(position);
    const targets = draft.targets.map((target, index) => (
      index === targetIndex ? { ...target, [field]: value } : target
    ));
    updateDraft(position, { targets });
  };

  const updateStoplossMovement = (position, targetIndex, value) => {
    const draft = getDraft(position);
    const targetCount = Math.max(draft.targets.length, 1);
    const nextPlan = normalizeStoplossPlan(draft.stoploss_movement_plan, targetCount);
    nextPlan[targetIndex] = value;
    updateDraft(position, {
      stoploss_movement_plan: normalizeStoplossPlan(nextPlan, targetCount),
    });
  };

  const addTarget = (position) => {
    const draft = getDraft(position);
    const maxTargets = Math.max(1, Number(tradeConfig.target_count) || 1);
    if (draft.targets.length >= maxTargets) {
      setError(`This stock cannot exceed the global target count of ${maxTargets}.`);
      setMessage(null);
      return;
    }
    const nextRatio = draft.targets.reduce((highest, target) => Math.max(highest, Number(target.ratio) || 0), 0) + 1;
    setError(null);
    updateDraft(position, {
      targets: [...draft.targets, { ratio: nextRatio, price: '', quantity: '', notification_sent_at: null }],
    });
  };

  const removeTarget = (position, targetIndex) => {
    const draft = getDraft(position);
    updateDraft(position, {
      targets: draft.targets.filter((_, index) => index !== targetIndex),
    });
  };

  const savePlan = async (position) => {
    const key = positionKey(position);
    const draft = getDraft(position);
    const entryPrice = Number(draft.entry_price);
    const initialStoploss = Number(draft.initial_stoploss_price);
    const trailingStoploss = Number(draft.trailing_stoploss_price);
    const targetPrices = draft.targets.map((target, index) => ({
      ratio: Number(target.ratio) || index + 1,
      price: Number(target.price),
      quantity: target.quantity === '' || target.quantity == null ? null : Number(target.quantity),
      error: target.error,
      notification_sent_at: target.notification_sent_at,
      notification_sent_price: target.notification_sent_price,
      live_execution_executed_at: target.live_execution_executed_at,
      live_execution_executed_price: target.live_execution_executed_price,
      live_execution_executed_quantity: target.live_execution_executed_quantity,
      live_execution_order_id: target.live_execution_order_id,
      live_execution_error: target.live_execution_error,
    }));
    const openQuantity = getPlanQuantity(position);
    const plannedQuantity = targetPrices.reduce((sum, target) => (
      target.live_execution_executed_at ? sum : sum + Math.max(0, Number(target.quantity || 0) - Number(target.live_execution_executed_quantity || 0))
    ), 0);

    setError(null);
    setMessage(null);
    if (!Number.isFinite(initialStoploss) || initialStoploss <= 0 || !Number.isFinite(trailingStoploss) || trailingStoploss <= 0) {
      setError('Enter valid initial and trailing stop-loss prices.');
      return;
    }
    if (targetPrices.some((target) => (
      !Number.isFinite(target.price)
      || target.price <= 0
      || (target.quantity != null && (!Number.isInteger(target.quantity) || target.quantity <= 0))
    ))) {
      setError('Every target needs a valid price, and allocated quantities must be positive whole numbers.');
      return;
    }
    const isLongPosition = getQuantity(position) > 0;
    const orderedTargets = targetPrices
      .filter((target) => Number.isFinite(target.price) && target.price > 0)
      .sort((left, right) => Number(left.ratio) - Number(right.ratio));
    const targetOrderInvalid = orderedTargets.some((target, index) => {
      if (index === 0) return false;
      return isLongPosition
        ? Number(target.price) <= Number(orderedTargets[index - 1].price)
        : Number(target.price) >= Number(orderedTargets[index - 1].price);
    });
    if (targetOrderInvalid) {
      setError(isLongPosition
        ? 'For a LONG position, targets must increase from T1 to T4.'
        : 'For a SHORT position, targets must decrease from T1 to T4.');
      return;
    }
    if (!Number.isFinite(entryPrice) || entryPrice <= 0) {
      setError('Enter a valid entry price.');
      return;
    }
    if ((isLongPosition && initialStoploss >= entryPrice) || (!isLongPosition && initialStoploss <= entryPrice)) {
      setError(isLongPosition
        ? 'For a LONG position, the initial stop-loss must be below the entry price.'
        : 'For a SHORT position, the initial stop-loss must be above the entry price.');
      return;
    }
    const pendingTargets = orderedTargets.filter((target) => !target.live_execution_executed_at);
    const nextTarget = pendingTargets[0];
    if (nextTarget && ((isLongPosition && trailingStoploss >= Number(nextTarget.price))
      || (!isLongPosition && trailingStoploss <= Number(nextTarget.price)))) {
      setError(isLongPosition
        ? 'The active stop-loss must be below the next pending target for a LONG position.'
        : 'The active stop-loss must be above the next pending target for a SHORT position.');
      return;
    }
    if (plannedQuantity > openQuantity) {
      setError(`Target quantities cannot exceed the open quantity of ${openQuantity}.`);
      return;
    }
    if (targetPrices.length > Math.max(1, Number(tradeConfig.target_count) || 1)) {
      setError(`This stock cannot save more than the global target count of ${tradeConfig.target_count}.`);
      return;
    }
    setSaving((current) => ({ ...current, [key]: true }));
    try {
      await api.put(`/stoplosses/${encodeURIComponent(position.tradingsymbol)}`, {
        saved_stoploss: initialStoploss,
        initial_stoploss_price: initialStoploss,
        trailing_stoploss_price: trailingStoploss,
        target_prices: targetPrices,
        instrument_token: position.instrument_token,
        entry_price: entryPrice,
        avg_price: entryPrice,
        quantity: getPlanQuantity(position),
        exchange: position.exchange,
        product_type: position.product,
        asset_type: position.plan_asset_type || position.asset_type || assetType,
        order_type: getQuantity(position) > 0 ? 'BUY' : 'SELL',
        notifications_enabled: Boolean(draft.notifications_enabled),
        live_execution_enabled: Boolean(draft.live_execution_enabled),
        live_execution_mode: draft.live_execution_mode,
        ...(draft.notifications_enabled ? {
          notification_plan_closed: false,
          notification_error: null,
        } : {}),
        move_stoploss_automatically: Boolean(draft.move_stoploss_automatically),
        use_global_sl_movement: false,
        trailing_stoploss_mode: tradeConfig.trailing_stoploss_mode,
        stoploss_movement_plan: normalizeStoplossPlan(draft.stoploss_movement_plan, draft.targets.length),
      });
      setMessage(`${position.tradingsymbol} trade plan saved locally.`);
      setEditing((current) => {
        const next = { ...current };
        delete next[key];
        return next;
      });
      if (typeof onSaved === 'function') await onSaved();
    } catch (requestError) {
      setError(requestError.response?.data?.error || requestError.message || 'Save failed');
    } finally {
      setSaving((current) => ({ ...current, [key]: false }));
    }
  };

  const resetNotifications = async (position) => {
    const key = positionKey(position);
    if (!window.confirm(`Reset Telegram notification state for ${position.tradingsymbol}?`)) return;
    setError(null);
    setMessage(null);
    setResetting((current) => ({ ...current, [`${key}:notifications`]: true }));
    try {
      await api.post(`/stoplosses/${encodeURIComponent(position.tradingsymbol)}/reset-notifications`, {
        instrument_token: position.instrument_token,
        exchange: position.exchange,
        product_type: position.product,
        asset_type: position.plan_asset_type || position.asset_type || assetType,
      });
      setEditing((current) => {
        const next = { ...current };
        delete next[key];
        return next;
      });
      setMessage(`${position.tradingsymbol} notification plan reset. Telegram alerts can start fresh independently now.`);
      if (typeof onSaved === 'function') await onSaved();
    } catch (requestError) {
      setError(requestError.response?.data?.error || requestError.message || 'Notification reset failed');
    } finally {
      setResetting((current) => ({ ...current, [`${key}:notifications`]: false }));
    }
  };

  const exitPosition = async (position, fullExit = false) => {
    const key = positionKey(position);
    const draft = getDraft(position);
    const openQuantity = Math.abs(getQuantity(position));
    const quantity = fullExit ? openQuantity : Number(draft.exit_quantity);

    setError(null);
    setMessage(null);
    if (!Number.isInteger(quantity) || quantity <= 0 || quantity > openQuantity) {
      setError(`Exit quantity must be a whole number from 1 to ${openQuantity}.`);
      return;
    }
    if (!window.confirm(`Place a live market exit for ${quantity} ${position.tradingsymbol}?`)) return;

    setExiting((current) => ({ ...current, [key]: true }));
    try {
      await api.post(exitEndpoint, {
        tradingsymbol: position.tradingsymbol,
        exchange: position.exchange,
        product: position.product,
        quantity,
      });
      setMessage(`Live exit order placed for ${quantity} ${position.tradingsymbol}.`);
      if (typeof onSaved === 'function') await onSaved();
    } catch (requestError) {
      setError(requestError.response?.data?.error || requestError.message || 'Exit order failed');
    } finally {
      setExiting((current) => ({ ...current, [key]: false }));
    }
  };

  const cleanStaleLocalData = async () => {
    if (!window.confirm('Remove local stock plans that are no longer present in Kite holdings or open positions?')) return;
    setCleaning(true);
    setError(null);
    setMessage(null);
    try {
      const response = await api.post('/stoplosses/cleanup/stale');
      const removed = Number(response.data?.removed) || 0;
      setMessage(removed > 0 ? `Removed ${removed} stale local stock plan${removed === 1 ? '' : 's'}.` : 'Local data already matches Kite. Nothing stale was found.');
      if (typeof onSaved === 'function') await onSaved();
    } catch (requestError) {
      setError(requestError.response?.data?.error || requestError.message || 'Local cleanup failed');
    } finally {
      setCleaning(false);
    }
  };

  const resetAllStoplossAndTargets = async () => {
    if (!window.confirm('Clear saved stop-losses, targets, alert history, and live-execution settings for every plan? This does not place orders or change your Kite positions.')) return;
    setResettingAllPlans(true);
    setError(null);
    setMessage(null);
    try {
      const response = await api.post('/stoplosses/reset-all-plans');
      setMessage(response.data?.message || 'All stop-loss and target plans were reset.');
      if (typeof onSaved === 'function') await onSaved();
    } catch (requestError) {
      setError(requestError.response?.data?.error || requestError.message || 'Stop-loss and target reset failed');
    } finally {
      setResettingAllPlans(false);
    }
  };

  const resetStoplossAndTargets = async (position) => {
    const key = positionKey(position);
    if (!window.confirm(`Clear saved stop-losses, targets, alert history, and live-execution settings for ${position.tradingsymbol}? This does not place an order or change the Kite position.`)) return;
    setResetting((current) => ({ ...current, [`${key}:plan`]: true }));
    setError(null);
    setMessage(null);
    try {
      const response = await api.post(`/stoplosses/${encodeURIComponent(position.tradingsymbol)}/reset-plan`, {
        instrument_token: position.instrument_token,
        exchange: position.exchange,
        product_type: position.product,
        asset_type: position.plan_asset_type || position.asset_type || assetType,
      });
      setMessage(response.data?.message || `${position.tradingsymbol} stop-loss and target plan reset.`);
      if (typeof onSaved === 'function') await onSaved();
    } catch (requestError) {
      setError(requestError.response?.data?.error || requestError.message || 'Stop-loss and target reset failed');
    } finally {
      setResetting((current) => ({ ...current, [`${key}:plan`]: false }));
    }
  };

  const totalPnl = openPositions.reduce((sum, position) => sum + (Number(position.pnl) || 0), 0);

  return (
    <div className="card live-positions-card">
      <div className="card-header">
        <div>
          <div className="section-kicker">{kicker}</div>
          <h2>{title}</h2>
        </div>
        <div className="stock-card-header-actions">
          <button type="button" className="button button-secondary button-small" disabled={resettingAllPlans} onClick={resetAllStoplossAndTargets}>{resettingAllPlans ? 'Resetting...' : 'Reset all SL & targets'}</button>
          <button type="button" className="button button-secondary button-small" disabled={cleaning} onClick={cleanStaleLocalData}>{cleaning ? 'Cleaning...' : 'Clean stale local data'}</button>
          <div className={totalPnl >= 0 ? 'card-stat positive' : 'card-stat negative'}>
            <span>Live P&amp;L</span>
            <strong>&#8377;{formatNumber(totalPnl)}</strong>
            <small>{openPositions.length} open</small>
          </div>
        </div>
      </div>

      {error ? <div className="error">{error}</div> : null}
      {message ? <div className="success-message">{message}</div> : null}

      {loading && openPositions.length === 0 ? (
        <div className="table-skeleton"><span /><span /><span /></div>
      ) : openPositions.length === 0 ? (
        <div className="empty-state empty-state-compact">
          <div className="empty-icon">R</div>
          <strong>{emptyTitle}</strong>
          <p>{emptyText}</p>
        </div>
      ) : (
        <div className="live-position-list">
          {openPositions.map((position) => {
            const key = positionKey(position);
            const draft = getDraft(position);
            const quantity = getQuantity(position);
            const isLong = quantity > 0;
            const requiresNotificationReview = Boolean(position.notification_error);
            const liveFlow = getLiveExecutionFlow(position, draft);
            const liveDiagnostic = getLiveExecutionDiagnostic(position, liveFlow);
            const liveCheckStale = isLiveExecutionCheckStale(position);
            const executedQuantity = liveFlow.targetEntries.reduce((sum, target) => sum + (target.done ? (target.executedQuantity || 0) : 0), 0);
            const nextPendingTarget = liveFlow.targetEntries.find((target) => !target.done);
            const liveCheckPayload = {
              symbol: position.tradingsymbol,
              exchange: position.exchange,
              product: position.product,
              side: isLong ? 'LONG' : 'SHORT',
              quantity: getPlanQuantity(position),
              live_execution_enabled: Boolean(position.live_execution_enabled),
              ltp: position.live_execution_last_check_price ?? null,
              active_sl: liveFlow.stoploss.price ?? null,
              pending_targets: liveFlow.targetEntries.filter((target) => !target.done).map((target) => ({
                target: target.title,
                price: target.price,
                quantity: target.quantity,
              })),
              last_result: position.live_execution_last_result || null,
              last_action: position.live_execution_last_action || null,
              kite_order_request: position.live_execution_last_order_request || null,
              condition: liveDiagnostic.state,
              reason: liveDiagnostic.reason,
              checked_at: position.live_execution_last_check_at || null,
            };
            const isFlowExpanded = Boolean(flowExpanded[key]);
            const currentRiskAmount = getCurrentRiskAmount(position, draft);
            const liveRiskAmount = getLiveRiskAmount(position, draft);
            const isGttMode = false;

            return (
              <details className={`live-position live-position-collapsible ${position.settlement_review_required ? 'needs-review' : ''}`} key={key}>
                <summary className="live-position-summary live-position-summary-collapsible">
                  <div className="collapse-hint">
                    <span aria-hidden="true">+</span>
                  </div>
                  <div>
                    <div className="position-title">
                      <strong>{position.tradingsymbol}</strong>
                      <span className={`trade-side-pill ${isLong ? 'long' : 'short'}`}>{isLong ? 'LONG' : 'SHORT'}</span>
                    </div>
                    <div className="small">{String(position.product || '').toUpperCase() === 'CNC' && (position.combined_exchanges || []).length > 1 ? `${position.combined_exchanges.join(' + ')} &middot; ` : `${position.exchange} &middot; `}{position.product} &middot; Qty {Math.abs(quantity)}{String(position.product || '').toUpperCase() === 'CNC' && Number(position.combined_open_quantity) > 0 ? ` (${formatNumber(position.combined_holding_quantity)} held + ${formatNumber(position.combined_open_quantity)} open)` : ''}</div>
                  </div>
                  <div className="position-market-stat"><span>Avg.</span><strong>&#8377;{formatNumber(position.average_price)}</strong></div>
                  <div className="position-market-stat"><span>LTP Kite</span><strong>&#8377;{formatNumber(position.last_price ?? position.close_price)}</strong></div>
                  <div className="position-market-stat"><span>LTP Yahoo</span><strong>{getYahooSnapshotPrice(position) != null ? `₹${formatNumber(getYahooSnapshotPrice(position))}` : 'N/A'}</strong></div>
                  <div className="position-market-stat"><span>Live risk used</span><strong>{currentRiskAmount != null ? `Rs${formatNumber(currentRiskAmount)}` : '—'}</strong></div>
                  <div className="position-market-stat"><span>Live risk</span><strong>{liveRiskAmount != null ? `Rs${formatNumber(liveRiskAmount)}` : '—'}</strong></div>
                  <div className={`position-market-stat ${Number(position.pnl) >= 0 ? 'positive' : 'negative'}`}><span>P&amp;L</span><strong>&#8377;{formatNumber(position.pnl)}</strong></div>
                </summary>

                {position.settlement_review_required ? (
                  <div className="quantity-review-alert">
                    <div><strong>Holding quantity changed after CNC settlement</strong><span>Alerts remain active using the existing Holding SL and targets.</span></div>
                    <div className="quantity-review-numbers"><span>Configured <strong>{Math.abs(Number(position.configured_quantity) || 0)}</strong></span><span>Kite now <strong>{Math.abs(quantity)}</strong></span><span>New position plan <strong>{Math.abs(Number(position.settlement_position_plan?.quantity) || 0)}</strong></span></div>
                    <small>The newly settled quantity may not match the current target allocation. Review Stop-loss &amp; targets for the full {Math.abs(quantity)} quantity, then save. The older Position copy will be removed only after that save.</small>
                  </div>
                ) : position.settlement_plan_conflict ? (
                  <div className="quantity-review-alert">
                    <div><strong>Two saved CNC plans differed</strong><span>Live execution and notifications are paused until you confirm the intended plan.</span></div>
                    <div className="quantity-review-numbers"><span>Open plan SL <strong>{position.settlement_plan_conflict.position_plan?.trailing_stoploss_price ?? position.settlement_plan_conflict.position_plan?.saved_stoploss ?? '—'}</strong></span><span>Holding plan SL <strong>{position.settlement_plan_conflict.holding_plan?.trailing_stoploss_price ?? position.settlement_plan_conflict.holding_plan?.saved_stoploss ?? '—'}</strong></span><span>Selected <strong>{position.settlement_plan_conflict.selected_asset_type || 'saved plan'}</strong></span></div>
                    <small>Review the displayed SL and targets. Edit if needed, then save this plan to resolve the conflict and resume the enabled monitor.</small>
                  </div>
                ) : position.quantity_inconsistent ? (
                  <div className="quantity-review-alert quantity-review-alert-soft">
                    <div><strong>Target allocation does not match Kite quantity</strong><span>Update the pending target quantities in this plan and save. Completed targets stay recorded; you do not need to reset or recreate the plan.</span></div>
                    <div className="quantity-review-numbers"><span>Pending target quantity <strong>{Math.abs(Number(position.expected_remaining_quantity ?? position.configured_quantity) || 0)}</strong></span><span>Open CNC buy qty <strong>{Number(position.combined_open_quantity) || 0}</strong></span><span>Holdings <strong>{Number(position.combined_holding_quantity) || 0}</strong></span><span>Kite total <strong>{Number(position.combined_quantity) || 0}</strong></span></div>
                  </div>
                ) : null}

                <div className="stock-config-sections">
                  <section className={`stock-config-block notification-block ${isExpanded(position, 'notifications') ? 'is-open' : ''}`}>
                    <button type="button" className="stock-config-header" onClick={() => toggleSection(position, 'notifications')} aria-expanded={isExpanded(position, 'notifications')}>
                      <span className="config-icon">N</span>
                      <span className="config-heading"><strong>Notifications</strong><small>Telegram delivery and alert state</small></span>
                      <span className={`plan-status-pill ${notificationStatusClass(position)}`}>{notificationStatusLabel(position)}</span>
                      <span className="config-chevron">{isExpanded(position, 'notifications') ? '−' : '+'}</span>
                    </button>
                    {isExpanded(position, 'notifications') ? (
                      <div className="stock-config-body">
                        <div className="notification-overview">
                          <div><span>Telegram delivery</span><strong>{position.stoploss_notification_sent_at ? `SL delivered at Rs${formatNumber(position.stoploss_notification_sent_price)}` : (position.notification_last_result || 'No attempt yet')}</strong><small>{formatDateTime(position.stoploss_notification_sent_at || position.notification_last_attempt_at)}</small></div>
                          <div><span>Monitor</span><strong>{position.last_checked_at ? `Last price Rs${formatNumber(position.last_checked_price)}` : 'Waiting for first check'}</strong><small>{formatDateTime(position.last_checked_at)}</small></div>
                        </div>
                        <label className="config-switch"><span><strong>Send Telegram alerts</strong><small>Notify when a saved SL or target price is reached.</small></span><input type="checkbox" checked={draft.notifications_enabled} onChange={(event) => updateDraft(position, { notifications_enabled: event.target.checked })} /></label>
                        {position.notification_error ? <div className="automation-warning">{position.notification_error}</div> : null}
                        <div className="config-footer">
                          <button type="button" className="button button-quiet button-small" disabled={Boolean(resetting[`${key}:notifications`])} onClick={() => resetNotifications(position)}>{resetting[`${key}:notifications`] ? 'Resetting...' : (requiresNotificationReview ? 'Reset after review' : 'Reset alert history')}</button>
                          <button type="button" className="button button-primary button-small" disabled={saving[`${key}:notifications`]} onClick={() => saveNotifications(position)}>{saving[`${key}:notifications`] ? 'Saving...' : 'Save notifications'}</button>
                        </div>
                      </div>
                    ) : null}
                  </section>

                  <section className={`stock-config-block live-execution-block ${isExpanded(position, 'liveExecution') ? 'is-open' : ''}`}>
                    <button type="button" className="stock-config-header" onClick={() => toggleSection(position, 'liveExecution')} aria-expanded={isExpanded(position, 'liveExecution')}>
                      <span className="config-icon">LE</span>
                      <span className="config-heading"><strong>Live execution</strong><small>Automatic exit orders, logs, and errors</small></span>
                      <span className={`plan-status-pill ${liveExecutionStatusClass(position)}`}>{liveExecutionStatusLabel(position)}</span>
                      <span className="config-chevron">{isExpanded(position, 'liveExecution') ? '-' : '+'}</span>
                    </button>
                    {isExpanded(position, 'liveExecution') ? (
                      <div className="stock-config-body">
                        <div className="notification-overview">
                          <div>
                            <span>Monitor last check</span>
                            <strong>{position.live_execution_last_check_at ? `LTP Rs${formatNumber(position.live_execution_last_check_price)}` : 'Waiting for first execution check'}</strong>
                            <small className={liveCheckStale ? 'monitor-stale' : ''}>{formatTimestamp(position.live_execution_last_check_at)}{liveCheckStale ? ' · Update overdue' : ''}</small>
                          </div>
                          <div>
                            <span>Execution status</span>
                            <strong>{position.live_execution_last_result || 'No execution yet'}</strong>
                            <small>{position.live_execution_last_action || 'No action recorded'}</small>
                          </div>
                        </div>
                        <div className={`live-execution-diagnostic diagnostic-${liveDiagnostic.state.toLowerCase()}`}>
                          <strong>Condition check: {liveDiagnostic.state}</strong>
                          <span>{liveDiagnostic.reason}</span>
                          <small className={liveCheckStale ? 'monitor-stale' : ''}>Last checked: {formatTimestamp(position.live_execution_last_check_at)}{liveCheckStale ? ' · Backend check overdue' : ''}</small>
                          <details className="live-check-json"><summary>View check JSON</summary><pre>{JSON.stringify(liveCheckPayload, null, 2)}</pre></details>
                        </div>
                        <section className="execution-status-panel" aria-label="Target execution status">
                          <div className="execution-status-heading">
                            <div><strong>Target execution status</strong><span>Actual Kite exit order status for this plan.</span></div>
                            <small>Exited {executedQuantity} · Kite remaining {Math.abs(quantity)}{nextPendingTarget ? ` · Next ${nextPendingTarget.title} Rs${formatNumber(nextPendingTarget.price)}` : ' · All targets completed'}</small>
                          </div>
                          <div className="table-wrap execution-status-table-wrap">
                            <table className="table execution-status-table">
                              <thead><tr><th>Level</th><th>Price</th><th>Qty</th><th>Status</th><th>Executed at</th><th>Order</th></tr></thead>
                              <tbody>
                                {liveFlow.targetEntries.map((target) => {
                                  const status = target.done
                                    ? 'Achieved — filled'
                                    : (target.executionError ? 'Failed — review needed' : (target.executedQuantity ? `Partially filled ${target.executedQuantity}/${target.quantity}` : 'Pending'));
                                  return <tr key={`status-${target.key}`} className={target.done ? 'is-executed' : (target.executionError ? 'is-failed' : '')}>
                                    <td data-label="Level"><strong>{target.title}</strong></td>
                                    <td data-label="Price">Rs{formatNumber(target.price)}</td>
                                    <td data-label="Qty">{target.done ? (target.executedQuantity || target.quantity || '—') : (target.executedQuantity ? `${target.executedQuantity} filled / ${target.quantity}` : (target.quantity || '—'))}</td>
                                    <td data-label="Status"><span className={`execution-status-badge ${target.done ? 'is-achieved' : (target.executionError ? 'is-failed' : 'is-pending')}`}>{status}</span></td>
                                    <td data-label="Executed at">{target.executedAt ? formatTimestamp(target.executedAt) : '—'}</td>
                                    <td data-label="Order">{target.orderId ? `#${target.orderId}` : '—'}</td>
                                  </tr>;
                                })}
                                <tr className={liveFlow.stoploss.done ? 'is-executed' : ''}>
                                  <td data-label="Level"><strong>Stop-loss</strong></td>
                                  <td data-label="Price">{liveFlow.stoploss.price > 0 ? `Rs${formatNumber(liveFlow.stoploss.price)}` : '—'}</td>
                                  <td data-label="Qty">{liveFlow.stoploss.done ? Math.abs(quantity) : `Remaining ${liveFlow.stoploss.remaining ?? Math.abs(quantity)}`}</td>
                                  <td data-label="Status"><span className={`execution-status-badge ${liveFlow.stoploss.done ? 'is-achieved' : 'is-pending'}`}>{liveFlow.stoploss.done ? 'Stop-loss filled' : 'Armed'}</span></td>
                                  <td data-label="Executed at">{liveFlow.stoploss.done ? formatTimestamp(position.live_execution_stoploss_executed_at) : '—'}</td>
                                  <td data-label="Order">{position.live_execution_stoploss_order_id ? `#${position.live_execution_stoploss_order_id}` : '—'}</td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        </section>
                        {isGttMode ? (
                          <div className="notification-overview">
                            <div>
                              <span>Active Kite GTT</span>
                              <strong>{position.gtt_trigger_id ? `#${position.gtt_trigger_id}` : 'Not armed yet'}</strong>
                              <small>{position.gtt_status || 'Worker will arm after save'}</small>
                            </div>
                            <div>
                              <span>Current GTT legs</span>
                              <strong>
                                {position.gtt_current_stoploss_price ? `SL Rs${formatNumber(position.gtt_current_stoploss_price)}` : 'SL pending'}
                                {position.gtt_current_target_price ? ` + T${position.gtt_current_target_ratio || ''} Rs${formatNumber(position.gtt_current_target_price)}` : ''}
                              </strong>
                              <small>{position.gtt_remaining_quantity != null ? `Remaining qty ${position.gtt_remaining_quantity}` : 'Remaining qty pending'}</small>
                            </div>
                          </div>
                        ) : null}
                        {isGttMode ? (
                          <div className="gtt-clarity-panel">
                            <div>
                              <span>Actual active GTT stop-loss</span>
                              <strong>
                                {Number.isFinite(Number(position.gtt_current_stoploss_price ?? position.trailing_stoploss_price ?? position.saved_stoploss)) && Number(position.gtt_current_stoploss_price ?? position.trailing_stoploss_price ?? position.saved_stoploss) > 0
                                  ? `Rs${formatNumber(position.gtt_current_stoploss_price ?? position.trailing_stoploss_price ?? position.saved_stoploss)}`
                                  : 'Not armed yet'}
                              </strong>
                              <small>This is the live SL value currently used for Kite GTT execution.</small>
                            </div>
                            <div>
                              <span>Actual next target leg</span>
                              <strong>
                                {position.gtt_current_target_price
                                  ? `T${position.gtt_current_target_ratio || '-'} at Rs${formatNumber(position.gtt_current_target_price)}`
                                  : (getNextPendingTarget(position)
                                    ? `T${getNextPendingTarget(position).ratio || '-'} pending at Rs${formatNumber(getNextPendingTarget(position).price)}`
                                    : 'No target leg armed')}
                              </strong>
                              <small>
                                {position.gtt_current_target_quantity != null
                                  ? `Current target qty ${position.gtt_current_target_quantity}`
                                  : 'Only stop-loss leg is active right now.'}
                              </small>
                            </div>
                            <div>
                              <span>Last SL move after target hit</span>
                              <strong>
                                {getLatestExecutedTarget(position)
                                  ? `After T${getLatestExecutedTarget(position).ratio}, SL moved to Rs${formatNumber(position.trailing_stoploss_price ?? position.saved_stoploss)}`
                                  : 'No target completed yet'}
                              </strong>
                              <small>
                                {getLatestExecutedTarget(position)?.live_execution_executed_at
                                  ? `Updated ${formatTimestamp(getLatestExecutedTarget(position).live_execution_executed_at)}`
                                  : 'The next completed target will move the live execution SL.'}
                              </small>
                            </div>
                          </div>
                        ) : null}
                        <label className="config-switch">
                          <span>
                            <strong>Enable Live execution order</strong>
                            <small>Place real Kite exits when stop-loss or target levels trigger.</small>
                          </span>
                          <input
                            type="checkbox"
                            checked={draft.live_execution_enabled}
                            onChange={(event) => updateDraft(position, { live_execution_enabled: event.target.checked })}
                          />
                        </label>
                        <div className="sl-fields-grid">
                          <label>Execution mode
                            <select value="MARKET" disabled>
                              <option value="MARKET">Market polling</option>
                            </select>
                          </label>
                        </div>
                        {position.live_execution_error ? <div className="automation-warning">{position.live_execution_error}</div> : null}
                        <div className="live-execution-flow">
                          <button type="button" className="target-editor-heading live-execution-flow-header" onClick={() => toggleFlow(position)}>
                            <div>
                              <strong>Execution flow chart</strong>
                              <span>Uses the configured Target levels and current execution state.</span>
                            </div>
                            <span className="config-chevron">{isFlowExpanded ? '−' : '+'}</span>
                          </button>
                          {isFlowExpanded ? (
                            <>
                          <div className="live-execution-flow-list">
                            <div className={`live-execution-flow-step ${liveFlow.currentStepIndex === 0 ? 'is-current' : ''} ${liveFlow.stoploss.done ? 'is-done' : 'is-pending'}`}>
                              <div>
                                <strong>{liveFlow.stoploss.title}</strong>
                                <span>{liveFlow.stoploss.status}</span>
                              </div>
                              <small>
                                {Number.isFinite(liveFlow.stoploss.price) && liveFlow.stoploss.price > 0 ? `Rs${formatNumber(liveFlow.stoploss.price)}` : 'Not set'}
                                {liveFlow.stoploss.remaining != null ? ` · rem ${liveFlow.stoploss.remaining}` : ''}
                              </small>
                            </div>
                            <div className="live-execution-flow-summary live-execution-flow-summary-compact">
                              <strong>{liveFlow.stoploss.moveLabel}</strong>
                              <small>Current stop-loss location after the latest hit.</small>
                            </div>
                            {liveFlow.targetEntries.map((step, index) => (
                              <React.Fragment key={step.key}>
                              <div className={`flow-connector ${index === 0 ? 'from-sl' : ''}`} aria-hidden="true" />
                              <div className={`live-execution-flow-step ${liveFlow.currentStepIndex === index + 1 ? 'is-current' : ''} ${step.done ? 'is-done' : 'is-pending'}`}>
                                <div>
                                  <strong>{step.title}</strong>
                                  <span>{step.status}</span>
                                </div>
                                <small>
                                  {Number.isFinite(step.price) && step.price > 0 ? `Rs${formatNumber(step.price)}` : 'Not set'}
                                  {step.quantity != null ? ` · qty ${step.quantity}` : ''}
                                  {step.remainingAfterTarget != null ? ` · rem ${step.remainingAfterTarget}` : ''}
                                </small>
                              </div>
                              <div className="live-execution-flow-summary live-execution-flow-summary-compact">
                                <strong>{step.slMoveLabel}</strong>
                                <small>SL action after this target is hit.</small>
                              </div>
                              </React.Fragment>
                            ))}
                          </div>
                          <div className="live-execution-flow-summary">
                            <strong>{liveFlow.summary}</strong>
                            <small>
                              {position.live_execution_last_check_price != null ? `Last check Rs${formatNumber(position.live_execution_last_check_price)}` : 'No live check yet'}
                            </small>
                          </div>
                            </>
                          ) : null}
                        </div>
                        <div className="config-footer">
                          <button type="button" className="button button-quiet button-small" disabled={Boolean(resetting[`${key}:liveExecution`])} onClick={() => resetLiveExecution(position)}>{resetting[`${key}:liveExecution`] ? 'Resetting...' : 'Reset live execution'}</button>
                          <button type="button" className="button button-primary button-small" disabled={saving[`${key}:liveExecution`]} onClick={() => saveLiveExecution(position)}>{saving[`${key}:liveExecution`] ? 'Saving...' : 'Save live execution'}</button>
                        </div>
                      </div>
                    ) : null}
                  </section>

                  <section className={`stock-config-block plan-block ${isExpanded(position, 'plan') ? 'is-open' : ''}`}>
                    <button type="button" className="stock-config-header" onClick={() => toggleSection(position, 'plan')} aria-expanded={isExpanded(position, 'plan')}>
                      <span className="config-icon">ST</span>
                      <span className="config-heading"><strong>Stop-loss &amp; targets</strong><small>Price levels and trailing notification behavior</small></span>
                      <span className="config-summary">SL Rs{formatNumber(draft.trailing_stoploss_price)} · {draft.targets.length} targets</span>
                      <span className="config-chevron">{isExpanded(position, 'plan') ? '−' : '+'}</span>
                    </button>
                    {isExpanded(position, 'plan') ? (
                      <div className="stock-config-body">
                        <div className="sl-fields-grid">
                          <label>Entry price<input type="number" min="0" step="0.01" value={draft.entry_price} onChange={(event) => updateDraft(position, { entry_price: event.target.value })} /></label>
                          <label>Initial stop-loss<input type="number" step="0.01" value={draft.initial_stoploss_price} onChange={(event) => updateDraft(position, { initial_stoploss_price: event.target.value })} /></label>
                          <label>Current notification SL<input type="number" step="0.01" value={draft.trailing_stoploss_price} onChange={(event) => updateDraft(position, { trailing_stoploss_price: event.target.value })} /></label>
                          {Number.isFinite(Number(marketSnapshots[buildQuoteKey(position.exchange, position.tradingsymbol || position.trading_symbol)]?.day_low)) ? <button type="button" className="button button-secondary button-small" onClick={() => { const value = String(marketSnapshots[buildQuoteKey(position.exchange, position.tradingsymbol || position.trading_symbol)]?.day_low); updateDraft(position, { initial_stoploss_price: value, trailing_stoploss_price: value }); }}>Use day low · Rs{formatNumber(marketSnapshots[buildQuoteKey(position.exchange, position.tradingsymbol || position.trading_symbol)]?.day_low)}</button> : null}
                        </div>
                        <label className="config-switch"><span><strong>Move notification SL after target alerts</strong><small>Changes only the next Telegram SL threshold. It never places an order.</small></span><input type="checkbox" checked={draft.move_stoploss_automatically} onChange={(event) => updateDraft(position, { move_stoploss_automatically: event.target.checked })} /></label>
                        <div className="target-editor">
                          <div className="target-editor-heading"><div><strong>Target levels</strong><span>Set the alert price, reference quantity, and next SL behavior.</span></div><button type="button" className="button button-secondary button-small" onClick={() => applyConfiguration(position)}>Apply global plan</button></div>
                          {draft.targets.map((target, index) => (
                            <div className="target-editor-row target-editor-row-rich" key={`${key}-target-${index}`}>
                              <span className={target.notification_sent_at ? 'target-state-done' : ''}>T{target.ratio || index + 1}</span>
                              <label>Alert price<input disabled={Boolean(target.notification_sent_at)} type="number" step="0.01" value={target.price} onChange={(event) => updateTarget(position, index, 'price', event.target.value)} /></label>
                              <label>Ref. qty<input disabled={Boolean(target.notification_sent_at)} type="number" step="1" min="1" value={target.quantity} onChange={(event) => updateTarget(position, index, 'quantity', event.target.value)} /></label>
                              <label>After alert<select disabled={Boolean(target.notification_sent_at) || !draft.move_stoploss_automatically} value={normalizeStoplossPlan(draft.stoploss_movement_plan, draft.targets.length)[index]} onChange={(event) => updateStoplossMovement(position, index, event.target.value)}>{STOPLOSS_MOVEMENT_ACTIONS.map((action) => <option key={action.value} value={action.value}>{action.label}</option>)}</select></label>
                              {target.notification_sent_at ? <div className="target-notification-state"><strong>SENT</strong><small>Rs{formatNumber(target.notification_sent_price)}</small></div> : <button type="button" className="delete-btn target-remove-button" aria-label={`Remove target T${target.ratio || index + 1}`} onClick={() => removeTarget(position, index)}>&times;</button>}
                            </div>
                          ))}
                          <div className="target-editor-actions"><small>Pending target quantity {draft.targets.reduce((sum, target) => sum + (target.live_execution_executed_at ? 0 : Math.max(0, Number(target.quantity || 0) - Number(target.live_execution_executed_quantity || 0))), 0)}; combined Kite quantity {getPlanQuantity(position)}. Each target exits only its remaining configured quantity.</small><button type="button" className="button button-secondary button-small" disabled={draft.targets.length >= Math.max(1, Number(tradeConfig.target_count) || 1)} onClick={() => addTarget(position)}>{draft.targets.length >= Math.max(1, Number(tradeConfig.target_count) || 1) ? 'Maximum reached' : '+ Add target'}</button></div>
                        </div>
                        <div className="config-footer"><button type="button" className="button button-quiet button-small" disabled={Boolean(resetting[`${key}:plan`])} onClick={() => resetStoplossAndTargets(position)}>{resetting[`${key}:plan`] ? 'Resetting...' : 'Reset SL & targets'}</button><button type="button" className="button button-primary button-small" disabled={saving[key]} onClick={() => savePlan(position)}>{saving[key] ? 'Saving...' : 'Save SL & targets'}</button></div>
                      </div>
                    ) : null}
                  </section>

                  <section className={`stock-config-block exit-block ${isExpanded(position, 'exit') ? 'is-open' : ''}`}>
                    <button type="button" className="stock-config-header" onClick={() => toggleSection(position, 'exit')} aria-expanded={isExpanded(position, 'exit')}><span className="config-icon">X</span><span className="config-heading"><strong>Live exit</strong><small>Manual market order through Kite</small></span><span className="config-summary danger-text">Manual action</span><span className="config-chevron">{isExpanded(position, 'exit') ? '-' : '+'}</span></button>
                    {isExpanded(position, 'exit') ? <div className="stock-config-body"><div className="position-exit-panel"><div><strong>Exit position</strong><small>Review quantity before placing the market order.</small></div><input aria-label={`Exit quantity for ${position.tradingsymbol}`} type="number" min="1" max={Math.abs(quantity)} step="1" value={draft.exit_quantity} onChange={(event) => updateDraft(position, { exit_quantity: event.target.value })} /><button type="button" className="button button-secondary button-small" disabled={exiting[key]} onClick={() => exitPosition(position, false)}>Partial exit</button><button type="button" className="button button-small exit-full-button" disabled={exiting[key]} onClick={() => exitPosition(position, true)}>{exiting[key] ? 'Placing...' : 'Exit full'}</button></div></div> : null}
                  </section>
                </div>
              </details>
            );
          })}
        </div>
      )}
    </div>
  );
}

